<template>
  <q-select multiple use-chips :model-value="displayTags" @remove="onTagRemove" :readonly="readonly" :disable="readonly"
    :options="[]" use-input fill-input input-debounce="0" @filter="filterFn" dense :input-style="{ minWidth: '200px' }"
    class="cascade-select">
    <template v-slot:selected>
      <q-chip v-for="tag in displayTags" :key="`${tag.type}-${tag.id}`" removable @remove="onTagRemove(tag)"
        :disable="readonly" color="primary" text-color="white" dense>
        {{ tag.label }}
      </q-chip>
    </template>

    <template v-slot:no-option>
      <q-tree ref="treeRef" :nodes="processedNodes" node-key="value" :expanded="expandedNodes" :ticked="tickedNodes"
        @update:ticked="onTickedUpdate" :no-connectors="true"
        :no-nodes-label="filterText ? $t('no_matches_found') : $t('no_data')" tick-strategy="leaf" :filter="treeFilter"
        :filter-method="customFilter" dense icon="fa-solid fa-caret-right">
        <template v-slot:default-header="prop">
          <div class="row items-center">
            <!-- 自定义折叠图标 -->
            <div v-if="prop.node.children && prop.node.children.length > 0" class="q-pr-xs"
              @click.stop="toggleNode(prop.node)">
            </div>

            <!-- 叶子节点复选框 -->
            <q-checkbox v-if="prop.node.is_leaf" :model-value="isLeafSelected(prop.node)"
              @update:model-value="toggleLeafNode(prop.node, $event)" :disable="readonly" @click.stop class="q-mr-sm" />

            <!-- 父节点或叶子节点 label -->
            <div class="text-body1"
              @click.stop="prop.node.is_leaf ? toggleLeafNode(prop.node, !isLeafSelected(prop.node)) : toggleNode(prop.node)">
              {{ prop.node.label }}
            </div>
          </div>
        </template>

      </q-tree>
    </template>
  </q-select>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from "vue-i18n";

export default {
  name: 'CascadeSelect',

  props: {
    data: {
      type: Array,
      required: true,
      default: () => []
    },
    selected: {
      type: Array,
      default: () => []
    },
    readonly: {
      type: Boolean,
      default: false
    },
  },

  emits: ['update:selected'],

  setup(props, { emit }) {
    const { t } = useI18n();

    const treeRef = ref(null);
    const allData = ref([]); // 初始化为空

    const expandedNodes = ref([]);
    const filterText = ref('');
    const tickedNodes = ref([]);
    const treeFilter = ref('');

    const safeSelected = computed(() => props.selected || []);

    // ---- 新增：父节点加 "p-" 前缀的递归处理 ----
    const processData = (nodes) => {
      return nodes.map(node => ({
        ...node,
        value: node.is_leaf ? node.value : `p-${node.value}`, // 父节点加前缀
        children: node.children ? processData(node.children) : []
      }))
    }

    // 处理后的树形数据
    const processedNodes = computed(() => allData.value);

    // 部门节点映射表
    const departmentNodesMap = computed(() => {
      const map = {};
      processedNodes.value.forEach(dept => {
        map[dept.value] = dept;
      });
      return map;
    });

    const isLeafSelected = (node) => {
      return tickedNodes.value.includes(node.value);
    };

    const toggleLeafNode = (node, state) => {
      if (props.readonly || !node.is_leaf) return;
      let newSelected = [...safeSelected.value];
      const index = newSelected.indexOf(node.value);

      if (state && index === -1) {
        newSelected.push(node.value);
      } else if (!state && index !== -1) {
        newSelected.splice(index, 1);
      }

      emit('update:selected', newSelected);
    };

    // 标签显示处理
    const displayTags = computed(() => {
      const tags = [];

      processedNodes.value.forEach(category => {
        if (!category.children || category.children.length === 0) return;

        const childValues = category.children.map(c => c.value);
        const allSelected = childValues.every(v => safeSelected.value.includes(v));

        if (allSelected) {
          tags.push({ id: category.value, label: category.label, type: 'category' });
        } else {
          category.children.forEach(c => {
            if (safeSelected.value.includes(c.value)) {
              tags.push({ id: c.value, label: c.label, type: 'leaf' });
            }
          });
        }
      });

      return tags;
    });

    const onTagRemove = (tag) => {
      if (props.readonly) return;

      let newSelected = [...safeSelected.value];

      if (tag.type === 'category') {
        const department = departmentNodesMap.value[tag.id];
        if (department && department.children) {
          const childValues = department.children.map(child => child.value);
          newSelected = newSelected.filter(val => !childValues.includes(val));
        }
      } else {
        newSelected = newSelected.filter(val => val !== tag.id);
      }

      emit('update:selected', newSelected);
    };

    const customFilter = (node, filter) => {
      if (!filter) return true;
      return node.label.toLowerCase().includes(filter.toLowerCase());
    };

    const filterFn = (val, update) => {
      filterText.value = val;
      treeFilter.value = val;
      update(() => { });
    };

    // 勾选状态更新
    const onTickedUpdate = (val) => {
      // 只保留叶子节点
      const leafValues = processedNodes.value
        .flatMap(cat => cat.children || [])
        .filter(c => c.is_leaf)
        .map(c => c.value);

      const selectedLeaves = val.filter(v => leafValues.includes(v));
      tickedNodes.value = selectedLeaves; // 只存叶子节点
      emit('update:selected', selectedLeaves);
    };


    const expandAllNodes = () => {
      expandedNodes.value = [];
      const expandAll = (nodes) => {
        nodes.forEach(node => {
          if (node.children && node.children.length > 0) {
            expandedNodes.value.push(node.value);
            expandAll(node.children);
          }
        });
      };
      expandAll(processedNodes.value);
    };

    const toggleNode = (node) => {
      if (node.children && node.children.length > 0) {
        const index = expandedNodes.value.indexOf(node.value);
        if (index > -1) expandedNodes.value.splice(index, 1);
        else expandedNodes.value.push(node.value);
      }
    };

    watch(() => props.selected, (newVal) => {
      tickedNodes.value = [...(newVal || [])];
    }, { immediate: true, deep: true });

    // ---- 修改：接收到 data 时直接处理父节点 id ----
    watch(() => props.data, (newVal) => {
      allData.value = processData(newVal); // 直接加前缀
      expandAllNodes();
    }, { immediate: true, deep: true });

    onMounted(() => {
      expandAllNodes();
    });

    return {
      treeRef,
      processedNodes,
      expandedNodes,
      tickedNodes,
      displayTags,
      filterText,
      treeFilter,
      isLeafSelected,
      toggleLeafNode,
      onTagRemove,
      filterFn,
      onTickedUpdate,
      customFilter,
      toggleNode
    };
  }
};
</script>

<style scoped>
.q-tree__node-header {
  padding: 6px 0;
  display: flex;
  align-items: center;
}

.q-tree__node:not(.is-leaf) .q-checkbox {
  display: none;
}

.q-checkbox {
  pointer-events: auto;
}

.text-body1 {
  font-size: 1rem;
  line-height: 1.5;
  cursor: pointer;
}

.cascade-select {
  min-height: 40px;
}

.q-icon {
  transition: all 0.3s ease;
  cursor: pointer;
}

.q-icon:hover {
  color: #1976d2;
  transform: scale(1.2);
}
</style>
